import React, { useState } from "react";
import {
  LayoutDashboard,
  BarChart3,
  Megaphone,
  Mail,
  Film,
  Video,
  Image,
  Building2,
  Shield,
  X,
  ChevronRight,
  Users,
  ChevronDown,
  UserCheck,
  Building,
  MapPin,
  UserX,
  UserPlus,
  Cake,
  Globe,
  Calendar,
  PartyPopper,
  LogOut,
  Settings,
  Store,
  HeartHandshake,
  Shirt,
  Briefcase,
  Wallet,
  Award,
} from "lucide-react";
import { cn } from "../lib/utils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";

function LogoutButton() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/pilot/login");
  };

  return (
    <Button
      variant="destructive"
      onClick={handleLogout}
      className="w-full justify-start"
    >
      <LogOut className="mr-2 h-4 w-4" />
      Logout
    </Button>
  );
}

function isRolesPath(pathname) {
  return (
    pathname.startsWith("/pilot/admins/directors") ||
    pathname.startsWith("/pilot/admins/sdms") ||
    pathname.startsWith("/pilot/admins/payroll") ||
    pathname.startsWith("/pilot/admins/pastors")
  );
}

function isPrimaryAdminsPath(pathname) {
  return pathname === "/pilot/admins" || pathname === "/pilot/admins/";
}

export function AdminSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const [staffMenuOpen, setStaffMenuOpen] = useState(
    location.pathname.startsWith("/pilot/staff")
  );
  const [rolesMenuOpen, setRolesMenuOpen] = useState(isRolesPath(location.pathname));
  const [manageMenuOpen, setManageMenuOpen] = useState(
    location.pathname.startsWith("/pilot/departments") ||
      location.pathname.startsWith("/pilot/regions") ||
      location.pathname.startsWith("/pilot/slides") ||
      isPrimaryAdminsPath(location.pathname) ||
      location.pathname.startsWith("/pilot/ranks") ||
      location.pathname.startsWith("/pilot/calendar-events") ||
      location.pathname.startsWith("/pilot/staff-counselling") ||
      location.pathname.startsWith("/pilot/staffweek-attire") ||
      location.pathname.startsWith("/pilot/app-version") ||
      location.pathname.startsWith("/pilot/analytics")
  );

  const staffSubMenuItems = [
    {
      icon: UserPlus,
      label: "Add New Staff",
      href: "/pilot/staff/new",
      active: location.pathname === "/pilot/staff/new",
    },
    {
      icon: Users,
      label: "All Staff",
      href: "/pilot/staff",
      active: location.pathname === "/pilot/staff",
    },
    {
      icon: UserCheck,
      label: "Active Staff",
      href: "/pilot/staff/active",
      active: location.pathname === "/pilot/staff/active",
    },
    {
      icon: UserX,
      label: "Disengaged Staff",
      href: "/pilot/staff/disengaged",
      active: location.pathname === "/pilot/staff/disengaged",
    },
    {
      icon: Building,
      label: "HQ Staff",
      href: "/pilot/staff/hq",
      active: location.pathname === "/pilot/staff/hq",
    },
    {
      icon: MapPin,
      label: "Lagos Staff",
      href: "/pilot/staff/lagos",
      active: location.pathname === "/pilot/staff/lagos",
    },
    {
      icon: Cake,
      label: "Staff Birthdays",
      href: "/pilot/staff/birthdays",
      active: location.pathname === "/pilot/staff/birthdays",
    },
  ];

  const rolesSubMenuItems = [
    {
      icon: Briefcase,
      label: "Directors",
      href: "/pilot/admins/directors",
      active: location.pathname.startsWith("/pilot/admins/directors"),
    },
    {
      icon: UserCheck,
      label: "SDM",
      href: "/pilot/admins/sdms",
      active: location.pathname.startsWith("/pilot/admins/sdms"),
    },
    {
      icon: Wallet,
      label: "Payroll",
      href: "/pilot/admins/payroll",
      active: location.pathname.startsWith("/pilot/admins/payroll"),
    },
    {
      icon: Users,
      label: "Zonal Pastors",
      href: "/pilot/admins/pastors",
      active: location.pathname.startsWith("/pilot/admins/pastors"),
    },
  ];

  const manageSubMenuItems = [
    {
      icon: Building2,
      label: "Departments",
      href: "/pilot/departments",
      active: location.pathname.startsWith("/pilot/departments"),
    },
    {
      icon: Globe,
      label: "Regions",
      href: "/pilot/regions",
      active: location.pathname.startsWith("/pilot/regions"),
    },
    {
      icon: Image,
      label: "Slides",
      href: "/pilot/slides",
      active: location.pathname.startsWith("/pilot/slides"),
    },
    {
      icon: Calendar,
      label: "Ministry Calendar",
      href: "/pilot/calendar-events",
      active: location.pathname.startsWith("/pilot/calendar-events"),
    },
    {
      icon: Shield,
      label: "Primary Admins",
      href: "/pilot/admins",
      active: isPrimaryAdminsPath(location.pathname),
    },
    {
      icon: Award,
      label: "Ranks",
      href: "/pilot/ranks",
      active: location.pathname.startsWith("/pilot/ranks"),
    },
    {
      icon: HeartHandshake,
      label: "Staff Counselling",
      href: "/pilot/staff-counselling",
      active: location.pathname.startsWith("/pilot/staff-counselling"),
    },
    {
      icon: Shirt,
      label: "Staff Week Attire",
      href: "/pilot/staffweek-attire",
      active: location.pathname.startsWith("/pilot/staffweek-attire"),
    },
    {
      icon: Settings,
      label: "App Updates",
      href: "/pilot/app-version",
      active: location.pathname.startsWith("/pilot/app-version"),
    },
    {
      icon: BarChart3,
      label: "Analytics",
      href: "/pilot/analytics",
      active: location.pathname.startsWith("/pilot/analytics"),
    },
  ];

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: "Dashboard",
      href: "/pilot/dashboard",
      active:
        location.pathname === "/pilot/dashboard" || location.pathname === "/pilot",
    },
    {
      icon: Users,
      label: "Staff Management",
      href: "/pilot/staff",
      active: location.pathname.startsWith("/pilot/staff"),
      hasSubmenu: true,
      submenuKey: "staff",
      submenu: staffSubMenuItems,
    },
    {
      icon: Megaphone,
      label: "Information Center",
      href: "/pilot/announcements",
      active: location.pathname.startsWith("/pilot/announcements"),
    },
    {
      icon: Mail,
      label: "Newsletters",
      href: "/pilot/newsletters",
      active: location.pathname.startsWith("/pilot/newsletters"),
    },
    {
      icon: Film,
      label: "Videos",
      href: "/pilot/videos",
      active: location.pathname.startsWith("/pilot/videos"),
    },
    {
      icon: Video,
      label: "Meetings",
      href: "/pilot/meetings",
      active: location.pathname.startsWith("/pilot/meetings"),
    },
    {
      icon: Calendar,
      label: "KingsPass Events",
      href: "/pilot/kingspass-events",
      active: location.pathname.startsWith("/pilot/kingspass-events"),
    },
    {
      icon: PartyPopper,
      label: "Staff Events",
      href: "/pilot/staff-events",
      active: location.pathname.startsWith("/pilot/staff-events"),
    },
    {
      icon: Store,
      label: "Marketplace",
      href: "/pilot/marketplace",
      active: location.pathname.startsWith("/pilot/marketplace"),
    },
    {
      icon: Briefcase,
      label: "Roles",
      href: "/pilot/admins/directors",
      active: isRolesPath(location.pathname),
      hasSubmenu: true,
      submenuKey: "roles",
      submenu: rolesSubMenuItems,
    },
    {
      icon: Settings,
      label: "Manage",
      href: "/pilot/departments",
      active:
        location.pathname.startsWith("/pilot/departments") ||
        location.pathname.startsWith("/pilot/regions") ||
        location.pathname.startsWith("/pilot/slides") ||
        isPrimaryAdminsPath(location.pathname) ||
        location.pathname.startsWith("/pilot/ranks") ||
        location.pathname.startsWith("/pilot/calendar-events") ||
        location.pathname.startsWith("/pilot/staff-counselling") ||
        location.pathname.startsWith("/pilot/staffweek-attire") ||
        location.pathname.startsWith("/pilot/app-version") ||
        location.pathname.startsWith("/pilot/analytics"),
      hasSubmenu: true,
      submenuKey: "manage",
      submenu: manageSubMenuItems,
    },
  ];

  const isSubmenuOpen = (key) => {
    if (key === "staff") return staffMenuOpen;
    if (key === "roles") return rolesMenuOpen;
    if (key === "manage") return manageMenuOpen;
    return false;
  };

  const toggleSubmenu = (key) => {
    if (key === "staff") setStaffMenuOpen((v) => !v);
    if (key === "roles") setRolesMenuOpen((v) => !v);
    if (key === "manage") setManageMenuOpen((v) => !v);
  };

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const renderNav = (onLinkClick) => (
    <>
      {menuItems.map((item, index) => (
        <div key={index}>
          {item.hasSubmenu ? (
            <div>
              <button
                type="button"
                onClick={() => toggleSubmenu(item.submenuKey)}
                className={cn(
                  "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all duration-200 group",
                  item.active
                    ? "bg-gradient-to-r from-[#1E74FD] to-blue-600 text-white shadow-md shadow-[#1E74FD]/30"
                    : "text-foreground hover:bg-[#1E74FD]/10 hover:text-[#1E74FD]"
                )}
              >
                <div className="flex items-center gap-3">
                  <item.icon
                    className={cn(
                      "h-5 w-5 transition-all duration-200",
                      item.active
                        ? "text-white"
                        : "text-muted-foreground group-hover:text-[#1E74FD]"
                    )}
                  />
                  <span
                    className={cn(
                      "font-medium text-sm transition-colors duration-200",
                      item.active ? "text-white font-semibold" : "text-foreground"
                    )}
                  >
                    {item.label}
                  </span>
                </div>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    isSubmenuOpen(item.submenuKey) ? "rotate-180" : "",
                    item.active ? "text-white" : "text-muted-foreground"
                  )}
                />
              </button>
              {isSubmenuOpen(item.submenuKey) && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-2">
                  {item.submenu.map((subItem, subIndex) => (
                    <Link
                      key={subIndex}
                      to={subItem.href}
                      onClick={onLinkClick}
                      className={cn(
                        "flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 group",
                        subItem.active
                          ? "bg-gradient-to-r from-[#1E74FD] to-blue-600 text-white shadow-md"
                          : "text-foreground hover:bg-[#1E74FD]/10 hover:text-[#1E74FD]"
                      )}
                    >
                      <subItem.icon
                        className={cn(
                          "h-4 w-4 transition-all duration-200",
                          subItem.active
                            ? "text-white"
                            : "text-muted-foreground group-hover:text-[#1E74FD]"
                        )}
                      />
                      <span
                        className={cn(
                          "font-medium text-sm transition-colors duration-200",
                          subItem.active
                            ? "text-white font-semibold"
                            : "text-foreground"
                        )}
                      >
                        {subItem.label}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Link
              to={item.href}
              onClick={onLinkClick}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group",
                item.active
                  ? "bg-gradient-to-r from-[#1E74FD] to-blue-600 text-white shadow-md shadow-[#1E74FD]/30"
                  : "text-foreground hover:bg-[#1E74FD]/10 hover:text-[#1E74FD]"
              )}
            >
              <item.icon
                className={cn(
                  "h-5 w-5 transition-all duration-200",
                  item.active
                    ? "text-white"
                    : "text-muted-foreground group-hover:text-[#1E74FD]"
                )}
              />
              <span
                className={cn(
                  "font-medium text-sm transition-colors duration-200",
                  item.active ? "text-white font-semibold" : "text-foreground"
                )}
              >
                {item.label}
              </span>
              {item.active && (
                <ChevronRight className="h-4 w-4 ml-auto text-white" />
              )}
            </Link>
          )}
        </div>
      ))}
    </>
  );

  return (
    <>
      <aside className="hidden lg:flex fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 flex-col border-r border-border z-40 shadow-lg bg-background">
        <nav className="flex-1 space-y-1 p-4 overflow-y-auto">{renderNav()}</nav>
        <div className="p-4 border-t border-border space-y-2">
          <LogoutButton />
          <div className="text-xs font-bold text-muted-foreground text-center">
            LW Staff Portal v3.0
          </div>
        </div>
      </aside>

      <div
        className={cn(
          "lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm transition-opacity duration-300",
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      <aside
        className={cn(
          "lg:hidden fixed left-0 top-0 h-full w-[280px] max-w-[85vw] flex-col border-r border-border z-50 flex shadow-2xl transition-transform duration-300 ease-out bg-background",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-[#1E74FD] to-blue-600">
          <h2 className="text-lg font-semibold text-white">Admin Menu</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-blue-700 transition-colors text-white"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 p-4 overflow-y-auto">{renderNav(onClose)}</nav>

        <div className="p-4 border-t border-border space-y-2">
          <LogoutButton />
          <div className="text-xs font-bold text-muted-foreground text-center">
            LW Staff Portal v3.0
          </div>
        </div>
      </aside>
    </>
  );
}

export default AdminSidebar;
